import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface InviteToOrgModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
}

const ROLES = [
  'Admin',
  'Warehouse Coordinator',
  'Warehouse Staff',
  'Viewer',
  'Picker',
  'Printer',
  'Catcher',
  'Packer',
  'CX',
] as const;

const INITIAL_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'Warehouse Staff' as string,
};

export default function InviteToOrgModal({ open, onOpenChange, organizationId, organizationName }: InviteToOrgModalProps) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [form, setForm] = useState(INITIAL_FORM);

  const invite = trpc.platform.inviteToOrg.useMutation({
    onSuccess: () => {
      toast({ title: 'Success', description: `User invited to ${organizationName}` });
      utils.platform.getOrganization.invalidate({ organizationId });
      onOpenChange(false);
      setForm(INITIAL_FORM);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to invite user',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    invite.mutate({
      organizationId,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      role: form.role as typeof ROLES[number],
    });
  };

  const isLoading = invite.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Invite a user to {organizationName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-firstName">First Name *</Label>
                <Input
                  id="inv-firstName"
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-lastName">Last Name *</Label>
                <Input
                  id="inv-lastName"
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-email">Email *</Label>
              <Input
                id="inv-email"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-role">Role *</Label>
              <Select
                value={form.role}
                onValueChange={v => setForm(f => ({ ...f, role: v }))}
                disabled={isLoading}
              >
                <SelectTrigger id="inv-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Inviting...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
